// Hook reutilizável para o limite de mensagens gratuitas
// Encapsula toda a lógica de verificação, incremento e modal
import { useState } from 'react';
import { canSendMessage, incrementMessageCount } from 'services/freeMessageLimit';
import useSubscriptionCheck from 'hooks/useSubscriptionCheck';

/**
 * Hook que controla o limite de 20 mensagens/dia para usuários gratuitos.
 *
 * @param {string} characterId - Identificador único do ChatBox (ex: 'Chat01')
 * @returns {{
 *   showLimitModal: boolean,
 *   setShowLimitModal: Function,
 *   remaining: number,
 *   isPremium: boolean,
 *   checkBeforeSend: () => Promise<boolean>, // true = pode enviar, false = bloqueado
 *   registerSent: () => Promise<void>        // chamar depois que mensagem foi enviada
 * }}
 */
const useFreeMessageLimit = (characterId) => {
    const [showLimitModal, setShowLimitModal] = useState(false);
    const [remaining, setRemaining] = useState(20);
    const { hasActiveSubscription } = useSubscriptionCheck();

    // Verifica se pode enviar — chamar antes do sendMessage real
    const checkBeforeSend = async () => {
        try {
            // Premium não tem limite
            if (hasActiveSubscription === true) return true;

            const check = await canSendMessage(characterId);
            if (!check.allowed) {
                setRemaining(0);
                setShowLimitModal(true);
                return false;
            }
            return true;
        } catch (err) {
            // Em caso de erro, libera (fail-safe — não trava usuário pago se a checagem falhar)
            console.warn('Erro ao verificar limite, liberando envio:', err);
            return true;
        }
    };

    // Incrementa contador — chamar APÓS o envio bem-sucedido
    const registerSent = async () => {
        try {
            if (hasActiveSubscription === true) return;
            await incrementMessageCount(characterId);
        } catch (err) {
            console.warn('Erro ao incrementar contador:', err);
        }
    };

    return {
        showLimitModal,
        setShowLimitModal,
        remaining,
        isPremium: hasActiveSubscription === true,
        checkBeforeSend,
        registerSent
    };
};

export default useFreeMessageLimit;
